import { Tag } from "antd";
import React from "react";

export interface EnvironmentBadgeProps {
  /** The app's resolved deploy environment (e.g. `import.meta.env.VITE_DEPLOY_ENV`). Values with no matching entry below (`production`, `local`, undefined) render nothing. */
  env: string | undefined;
}

interface BadgeConfig {
  label: string;
  color: string;
}

/**
 * antd's built-in tag colors, so this reads as the same light-background/dark-text
 * language as the rest of each app rather than a new palette.
 *
 * The keys are build modes, not environment names — the two no longer line up:
 * the `staging` mode builds the QA environment and the `uat` mode builds UAT.
 * Only non-production environments get a badge — `production`/`local` are
 * intentionally absent so the badge disappears once a build is production-facing.
 */
const BADGE_CONFIG: Record<string, BadgeConfig> = {
  dev: { label: "Dogma QA", color: "gold" },
  staging: { label: "QA", color: "success" },
  uat: { label: "UAT", color: "processing" }
};

/**
 * Small pill rendered beside the app title in each app's top navbar, indicating a
 * non-production deploy environment. Unlike the full-width banner it replaced, this
 * lives inside the header itself rather than above it — see root CLAUDE.md's
 * "In-app environment indicator" for the placement rationale.
 */
export const EnvironmentBadge: React.FC<EnvironmentBadgeProps> = ({ env }) => {
  const config = env ? BADGE_CONFIG[env] : undefined;
  if (!config) return null;

  return (
    <Tag
      color={config.color}
      bordered={true}
      style={{ margin: 0, textTransform: "uppercase" }}
    >
      {config.label} instance
    </Tag>
  );
};
